import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { notificationService, notificationPreferenceService } from '@/shared/services/notificationService';
import { supabase } from '@/shared/services/supabaseClient';
import { useAuthStore } from '@/shared/stores/authStore';

const UNREAD_KEY = ['notifications', 'unread-count'];
const RECENT_KEY = ['notifications', 'recent'];
const PREFS_KEY = ['notification-preferences'];

export function useUnreadCount() {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000, // Poll every 30s
  });
}

export function useRecentNotifications(limit = 20) {
  return useQuery({
    queryKey: [...RECENT_KEY, limit],
    queryFn: () => notificationService.getRecent(limit),
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
      void queryClient.invalidateQueries({ queryKey: RECENT_KEY });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
      void queryClient.invalidateQueries({ queryKey: RECENT_KEY });
    },
  });
}

export function useNotificationPreferences() {
  const orgId = useAuthStore((s) => s.organizationId);
  return useQuery({
    queryKey: [...PREFS_KEY, orgId],
    queryFn: () => notificationPreferenceService.getPreferences(),
    enabled: !!orgId,
  });
}

export function useUpdatePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { eventKey: string; updates: { channel_email?: boolean; channel_in_app?: boolean } }) =>
      notificationPreferenceService.updatePreference(params.eventKey, params.updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PREFS_KEY });
    },
  });
}

/**
 * Supabase Realtime subscription for instant notification delivery.
 * Call this once in a top-level component (e.g., AppLayout).
 */
export function useNotificationRealtime() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
          void queryClient.invalidateQueries({ queryKey: RECENT_KEY });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}
